interface SupabaseEnvSource {
  readonly VITE_SUPABASE_URL?: string;
  readonly VITE_SUPABASE_ANON_KEY?: string;
  readonly VITE_SUPABASE_SERVICE_ROLE_KEY?: string;
  readonly VITE_SUPABASE_PROJECT_REF?: string;
  readonly VITE_SUPABASE_DB_SCHEMA?: string;
  readonly VITE_ENABLE_SUPABASE_SYNC?: string;
}

export interface SupabaseEnvironmentState {
  url: string | null;
  anonKey: string | null;
  serviceRoleKey: string | null;
  projectRef: string | null;
  schema: string;
  isConfigured: boolean;
  usesServiceRole: boolean;
  syncEnabled: boolean;
}

export interface SupabaseClientConfig {
  url: string;
  apiKey: string;
  schema: string;
  /** User's JWT access token for authenticated requests */
  accessToken?: string;
}

function normalizeString(value: string | undefined | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeUrl(value: string | undefined | null): string | null {
  const normalized = normalizeString(value);
  if (!normalized) return null;
  return normalized.replace(/\/+$/, '');
}

function normalizeBoolean(value: string | undefined | null): boolean {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

export function readSupabaseEnvironment(
  env: SupabaseEnvSource = import.meta.env
): SupabaseEnvironmentState {
  const url = normalizeUrl(env.VITE_SUPABASE_URL);
  const anonKey = normalizeString(env.VITE_SUPABASE_ANON_KEY);
  const serviceRoleKey = normalizeString(env.VITE_SUPABASE_SERVICE_ROLE_KEY);
  const projectRef = normalizeString(env.VITE_SUPABASE_PROJECT_REF);
  const schema = normalizeString(env.VITE_SUPABASE_DB_SCHEMA) ?? 'public';
  const syncEnabled = normalizeBoolean(env.VITE_ENABLE_SUPABASE_SYNC);

  const apiKey = serviceRoleKey ?? anonKey;
  const isConfigured = Boolean(url && apiKey);

  return {
    url,
    anonKey,
    serviceRoleKey,
    projectRef,
    schema,
    isConfigured,
    usesServiceRole: Boolean(serviceRoleKey),
    syncEnabled,
  };
}

export function isSupabaseSyncEnabled(env: SupabaseEnvSource = import.meta.env): boolean {
  return readSupabaseEnvironment(env).syncEnabled;
}

export function createClientConfigFromEnv(
  env: SupabaseEnvSource = import.meta.env
): SupabaseClientConfig | null {
  const state = readSupabaseEnvironment(env);
  if (!state.isConfigured || !state.url) {
    return null;
  }

  const apiKey = state.serviceRoleKey ?? state.anonKey;
  if (!apiKey) {
    return null;
  }

  return {
    url: state.url,
    apiKey,
    schema: state.schema,
  };
}
